import { QRCodeSVG } from "qrcode.react";
import { format, parse } from "date-fns";

const GIOTTO_LOGO = "/giotto-logo.png";
const IBP_LOGO = "/ibp-logo.png";

const safeFormatDate = (d) => {
  if (!d) return "--/--/----";
  try {
    const date = d instanceof Date ? d : new Date(d);
    if (isNaN(date.getTime())) return "--/--/----";
    return format(date, "dd/MM/yyyy");
  } catch {
    return "--/--/----";
  }
};

export const LabelPreview = ({ data, printRef }) => {
  const { productName, weight, weightUnit, batchNumber, packDate, expirationDate } = data;
  const formattedExpDate = safeFormatDate(expirationDate);
  const formattedPackDate = safeFormatDate(packDate);
  const formattedWeight = weight ? `${weight} ${weightUnit || "lb"}` : "-- lb";

  const qrData = JSON.stringify({
    product: productName || "N/A",
    weight: formattedWeight,
    batch: batchNumber || "N/A",
    packed: formattedPackDate,
    exp: formattedExpDate,
  });

  return (
    <div className="print-zone" ref={printRef}>
      <div
        id="printable-label"
        data-testid="label-preview"
        className="label-container rounded-sm shadow-2xl border border-zinc-300 flex flex-col"
        style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
      >
        {/* Header with Logos */}
        <div className="bg-white px-4 pt-3 pb-1 flex items-center justify-between">
          <img
            src={IBP_LOGO}
            alt="IBP"
            className="h-10 w-auto object-contain"
            data-testid="label-ibp-logo"
          />
          <img
            src={GIOTTO_LOGO}
            alt="Giotto Carni"
            className="h-10 w-auto object-contain"
            data-testid="label-giotto-logo"
          />
        </div>

        {/* Branding text */}
        <div className="px-4 pb-1 text-center">
          <div className="text-[8px] tracking-[0.1em] uppercase text-zinc-500 leading-tight">
            IBP Produced for Giotto Carni
          </div>
          <div className="text-[7px] tracking-[0.08em] uppercase text-zinc-400 leading-tight">
            Exclusive Importer for Albania
          </div>
        </div>

        <div className="mx-4 border-t border-zinc-300" />

        {/* Product Name */}
        <div className="px-4 pt-2 pb-1">
          <div className="text-[8px] tracking-[0.12em] uppercase text-zinc-400 leading-none">PRODUCT</div>
          <div className="text-[6px] tracking-[0.1em] uppercase text-zinc-300 leading-none mt-px">PRODUKTI</div>
          <div
            className="text-xl font-bold uppercase tracking-tight leading-tight text-black mt-0.5"
            data-testid="label-product-name"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", minHeight: "24px" }}
          >
            {productName || "SELECT PRODUCT"}
          </div>
        </div>

        <div className="mx-4 border-t border-zinc-200" />

        {/* Weight & Dates Grid */}
        <div className="px-4 py-1.5 grid grid-cols-3 gap-2">
          <div>
            <div className="text-[8px] tracking-[0.12em] uppercase text-zinc-400 leading-none">NET WEIGHT</div>
            <div className="text-[6px] tracking-[0.1em] uppercase text-zinc-300 leading-none mt-px">PESHA NETO</div>
            <div className="text-sm font-bold font-mono tracking-wide text-black mt-0.5" data-testid="label-weight">
              {formattedWeight}
            </div>
          </div>
          <div>
            <div className="text-[8px] tracking-[0.12em] uppercase text-zinc-400 leading-none">PACK DATE</div>
            <div className="text-[6px] tracking-[0.1em] uppercase text-zinc-300 leading-none mt-px">DATA E PAKETIMIT</div>
            <div className="text-sm font-bold font-mono tracking-wide text-black mt-0.5" data-testid="label-pack-date">
              {formattedPackDate}
            </div>
          </div>
          <div>
            <div className="text-[8px] tracking-[0.12em] uppercase text-zinc-400 leading-none">USE BY</div>
            <div className="text-[6px] tracking-[0.1em] uppercase text-zinc-300 leading-none mt-px">DATA E SKADENCES</div>
            <div className="text-sm font-bold font-mono tracking-wide text-black mt-0.5" data-testid="label-expiration">
              {formattedExpDate}
            </div>
          </div>
        </div>

        <div className="mx-4 border-t border-zinc-200" />

        {/* Batch / Lot */}
        <div className="px-4 py-1.5">
          <div className="text-[8px] tracking-[0.12em] uppercase text-zinc-400 leading-none">BATCH / LOT NUMBER</div>
          <div className="text-[6px] tracking-[0.1em] uppercase text-zinc-300 leading-none mt-px">NUMRI I LOTIT</div>
          <div className="text-sm font-mono font-bold tracking-widest text-black mt-0.5" data-testid="label-batch">
            {batchNumber || "------"}
          </div>
        </div>

        <div className="mx-4 border-t border-zinc-200" />

        {/* QR Code + Scan Info */}
        <div className="px-4 py-1.5 flex items-center gap-3">
          <div className="bg-white p-1 border border-zinc-200 rounded-sm shrink-0" data-testid="label-qr-code">
            <QRCodeSVG value={qrData} size={52} level="M" includeMargin={false} />
          </div>
          <div className="flex-1">
            <div className="text-[7px] tracking-[0.12em] uppercase text-zinc-400 leading-tight">SCAN FOR DETAILS</div>
            <div className="text-[6px] text-zinc-300 leading-tight mt-0.5">
              Product info, weight, batch, dates.
            </div>
          </div>
        </div>

        <div className="mx-4 border-t border-zinc-200" />

        {/* Origin / Establishment */}
        <div className="px-4 py-1.5 text-center" data-testid="label-origin-info">
          <div className="text-[9px] font-bold tracking-[0.1em] uppercase text-black leading-tight">
            Produced in United States
          </div>
          <div className="text-[6px] tracking-[0.08em] uppercase text-zinc-400 leading-tight">
            Prodhuar ne Shtetet e Bashkuara
          </div>
          <div className="text-[9px] font-mono font-bold tracking-wider text-black leading-tight mt-0.5">
            Est. 245C
          </div>
        </div>

        {/* Footer */}
        <div className="bg-zinc-100 px-4 py-1 flex items-center justify-between mt-auto">
          <div>
            <div className="text-[7px] tracking-wider uppercase text-zinc-400 leading-tight">KEEP REFRIGERATED</div>
            <div className="text-[5px] tracking-wider uppercase text-zinc-300 leading-tight">MBAJE TE FTOHTE</div>
          </div>
          <div className="text-[7px] font-mono text-zinc-400">{format(new Date(), "yyyy-MM-dd HH:mm")}</div>
        </div>
      </div>
    </div>
  );
};
